import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  applyStoredConsent,
  initGtm,
  pushEvent,
  setUser,
  trackSpaPageView,
} from "./gtm";

function nearestTrackable(el: EventTarget | null): HTMLElement | null {
  if (!(el instanceof Element)) return null;
  return el.closest("a, button, [role='button'], [data-track]") as HTMLElement | null;
}

function clickPayload(el: HTMLElement): Record<string, unknown> {
  const text = (el.innerText || el.getAttribute("aria-label") || "").trim().slice(0, 120);
  const id = el.id || undefined;
  const classes = typeof el.className === "string" ? el.className.slice(0, 200) : undefined;
  const isLink = el.tagName === "A" || el.getAttribute("role") === "link";
  const href = el instanceof HTMLAnchorElement ? el.href : el.getAttribute("href");

  return {
    click_type: isLink ? "link" : "button",
    click_text: text || undefined,
    click_url: href || undefined,
    click_id: id,
    click_classes: classes || undefined,
    page_path: window.location.pathname,
  };
}

export default function AnalyticsProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    applyStoredConsent();
    let frame2 = 0;
    const frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => {
        initGtm(import.meta.env.VITE_GTM_ID as string | undefined);
      });
    });
    return () => {
      cancelAnimationFrame(frame1);
      cancelAnimationFrame(frame2);
    };
  }, []);

  useEffect(() => {
    trackSpaPageView(location.pathname + location.search, document.title);
  }, [location.pathname, location.search]);

  useEffect(() => {
    setUser(user?.id ?? null);
  }, [user?.id]);

  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      const el = nearestTrackable(event.target);
      if (!el) return;
      if (el.hasAttribute("disabled") || el.getAttribute("aria-disabled") === "true") {
        return;
      }
      pushEvent("click", clickPayload(el));
    }
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return children;
}
